import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExternalLinkAlt, faCrown, faUserCog, faKey } from "@fortawesome/free-solid-svg-icons";
import { Modal } from "antd";
import { toast } from "react-toastify";
import { API_BASE_URL } from "../../config/api";

function Navbar() {
  const adminName = localStorage.getItem("adminName") || "Admin";
  const adminRole = localStorage.getItem("adminRole") || "sub";
  const adminId = localStorage.getItem("adminId");

  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (!newPassword || newPassword.length < 4) {
      toast.error("New password must be at least 4 characters.");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admins/${adminId}/password`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: newPassword })
      });

      if (res.ok) {
        toast.success("Password changed successfully!");
        setPasswordModalOpen(false);
        setNewPassword("");
        setConfirmPassword("");
      } else {
        const data = await res.json();
        toast.error(data.error || "Failed to update password.");
      }
    } catch (err) {
      toast.error("Server error while updating password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <nav
        style={{
          background: "#ffffff",
          borderBottom: "1px solid #e2e8f0",
          padding: "0 28px",
          height: "64px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
          position: "sticky",
          top: 0,
          zIndex: 1000
        }}
      >
        {/* Left: Logo & Title */}
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <img
            src="/consortiumelearning.png"
            alt="Consortium Logo"
            style={{ height: "32px", width: "auto", objectFit: "contain" }}
          />
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span
              style={{
                fontFamily: "'Outfit', sans-serif",
                fontWeight: 800,
                fontSize: "1.15rem",
                color: "#0f172a",
                lineHeight: "1.2",
                letterSpacing: "-0.01em"
              }}
            >
              Consortium E-Learning Admin
            </span>
            <span
              style={{
                background: "#e0e7ff",
                color: "#4f46e5",
                fontSize: "0.7rem",
                fontWeight: 800,
                padding: "2px 8px",
                borderRadius: "12px",
                textTransform: "uppercase"
              }}
            >
              Portal
            </span>
          </div>
        </div>

        {/* Right: Logged-in admin info + change password + public site link */}
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {/* Admin Badge */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              background: adminRole === "main" ? "#fef3c7" : "#f1f5f9",
              padding: "6px 14px",
              borderRadius: "10px",
              border: adminRole === "main" ? "1px solid #fde68a" : "1px solid #e2e8f0"
            }}
          >
            <FontAwesomeIcon
              icon={adminRole === "main" ? faCrown : faUserCog}
              style={{ color: adminRole === "main" ? "#d97706" : "#4f46e5", fontSize: "0.85rem" }}
            />
            <span style={{ fontSize: "0.82rem", fontWeight: 700, color: "#334155" }}>
              {adminName}
            </span>
            <span
              style={{
                fontSize: "0.68rem",
                fontWeight: 700,
                color: adminRole === "main" ? "#92400e" : "#4f46e5",
                textTransform: "uppercase"
              }}
            >
              ({adminRole === "main" ? "Main" : "Sub"})
            </span>
          </div>

          {/* Change Password Button */}
          <button
            onClick={() => setPasswordModalOpen(true)}
            style={{
              background: "#e0e7ff",
              color: "#4f46e5",
              border: "none",
              padding: "8px 14px",
              borderRadius: "10px",
              fontSize: "0.82rem",
              fontWeight: 700,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              transition: "background 0.2s ease"
            }}
          >
            <FontAwesomeIcon icon={faKey} /> Change Password
          </button>

          <Link
            to="/"
            style={{
              background: "#f1f5f9",
              color: "#334155",
              padding: "8px 16px",
              borderRadius: "10px",
              fontSize: "0.85rem",
              fontWeight: 700,
              textDecoration: "none",
              display: "flex",
              alignItems: "center",
              gap: "6px"
            }}
          >
            View Public Site <FontAwesomeIcon icon={faExternalLinkAlt} style={{ fontSize: "0.75rem" }} />
          </Link>
        </div>
      </nav>

      {/* Change Password Modal */}
      <Modal
        title="Change Admin Password"
        open={passwordModalOpen}
        onCancel={() => setPasswordModalOpen(false)}
        footer={null}
      >
        <form onSubmit={handlePasswordChange} style={{ display: "flex", flexDirection: "column", gap: "16px", paddingTop: "10px" }}>
          <div>
            <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#334155", display: "block", marginBottom: "6px" }}>New Password</label>
            <input
              type="password"
              placeholder="Enter new password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: "1.5px solid #cbd5e1", boxSizing: "border-box" }}
              required
            />
          </div>
          <div>
            <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#334155", display: "block", marginBottom: "6px" }}>Confirm New Password</label>
            <input
              type="password"
              placeholder="Re-enter new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: "1.5px solid #cbd5e1", boxSizing: "border-box" }}
              required
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            style={{
              background: "linear-gradient(135deg, #4f46e5 0%, #3730a3 100%)",
              color: "#fff",
              border: "none",
              padding: "12px",
              borderRadius: "10px",
              fontWeight: 700,
              cursor: loading ? "wait" : "pointer",
              marginTop: "6px"
            }}
          >
            {loading ? "Updating..." : "Update Password"}
          </button>
        </form>
      </Modal>
    </>
  );
}

export default Navbar;